<template>
  <Card :title="t('participants.participants') + `\t(${participants.length}/${maxParticipants})`">
    <DataTable
        :headers="participantsHeaders"
        :is-loading="participantsAreLoading"
        :items="participantsItems"
        :search-value="participantsSearchValue"
        @edit="handleEdit"/>
    <NewActivityParticipant
        v-model:activityOccurrenceId="activityOccurrenceId"
        v-model:isOnlineActivity="isOnline"
        @newParticipantAdded="() => emit('newParticipantAdded')"/>
  </Card>
</template>

<script lang="ts" setup>
import NewActivityParticipant from "@/components/activities/NewActivityParticipant.vue";
import DataTable from "@/components/layout/DataTable.vue";
import Card from "@/components/layout/Card.vue";
import {computed, defineEmits, ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import {Participant} from "@/types/entities/activity";
import {Item} from "vue3-easy-data-table";

const {t} = useI18n();

// eslint-disable-next-line
interface propsInterface {
  activityId: string
  activityOccurrenceId: string,

  participants: Participant[],
  maxParticipants?: number,
  participantsAreLoading: boolean,
  participantsSearchValue: string
  isOnlineActivity: boolean,
}

// eslint-disable-next-line no-undef
const props = defineProps<propsInterface>();

const emit = defineEmits<{
  // states that the event has to be called 'update:modelValue'
  (event: "newParticipantAdded"): void;
}>();

const participants = ref(props.participants);
const activityOccurrenceId = ref(props.activityOccurrenceId);
const activityId = ref(props.activityId);
const participantsAreLoading = ref(props.participantsAreLoading);
const participantsSearchValue = ref(props.participantsSearchValue)
const isOnline = ref(props.isOnlineActivity);
const maxParticipants = ref(props.maxParticipants ?? 0);

watch(props, (newProps) => {
  activityId.value = newProps.activityId;
  activityOccurrenceId.value = newProps.activityOccurrenceId;
  participants.value = newProps.participants;
  participantsAreLoading.value = newProps.participantsAreLoading;
  participantsSearchValue.value = newProps.participantsSearchValue;
  isOnline.value = newProps.isOnlineActivity;
  maxParticipants.value = newProps.maxParticipants!;
});


const participantsItems = computed(() => {
  let items: Item[] = [];
  if (participants.value == null || participants.value.length == 0)
    return [];
  participants.value.forEach((participant: Participant) => {
    items.push({
      ...participant,
      memberNumber: participant.memberNumber?.length ? participant.memberNumber : t("global.undefined"),
      reducedMobility: participant.reducedMobility ? t('global.yes') : t('global.no'),
      actions: {
        edit: true,
        transfer: {
          name: "admin.children.activities.participantTransfer",
          params: {participantId: participant.id, activityOccurrenceId: activityOccurrenceId.value}
        }
      }
    });
  });
  return items
})

const participantsHeaders = computed(() => {
  let headers =
    [
      {
        text: t("participants.memberNumber"),
        value: "memberNumber",
        sortable: true,
        width: 250
      },
      {
        text: t("participants.lastName"),
        value: "lastName",
        sortable: true
      },
      {
        text: t("participants.firstName"),
        value: "firstName",
        sortable: true
      },
      {
        text: t("participants.email"),
        value: "emailAddress",
        sortable: true
      }
    ];

  if (!isOnline.value) {
    headers.push(
      {
        text: t("participants.allergies"),
        value: "allergies",
        sortable: true
      },
      {
        text: t("participants.reducedMobility"),
        value: "reducedMobility",
        sortable: true
      },
    )
  }

  headers.push(
    {
      text: t("global.table.actions"),
      value: "actions",
      sortable: false
    })
  return headers
})

function handleEdit(id: string) {
  let participant = participants.value.find(x => x.id == id);
  if (!participant)
    return;
  const appUrl = new URL(window.location.href);
  let url = new URL(`${appUrl.origin}/activites-et-formations/inscription`);
  const params = new URLSearchParams({
    activityOccurrenceId: activityOccurrenceId.value,
    activityRegistrationId: participant.activityRegistrationId!
  });
  url.search = params.toString();
  window.location.href = url.toString();
}
</script>